import type { RootState } from 'src/redux/store';

import {
  addAuditFields,
  formatPhoneNumber,
  extractCustomFields,
  buildAssignedPayload,
} from 'src/utils/form-helpers';

import { useAppSelector } from 'src/hook/hooks';
import { useHandleForm } from 'src/hook/useHandleForm';
import { useAssigneeField } from 'src/hook/use-assignee-field';

import type { IAccount, IAccountCreate } from '../accounts.types';

interface UseAccountFormProps {
  open: boolean;
  selectedUser?: IAccount;
  handleSubmitData: (data: any, assignedPayload: any) => void;
  customFields: any[] | [];
}

const fldList = [
  { name: 'name', required: true },
  { name: 'email', required: true },
  { name: 'phone', required: true },
  { name: 'crNumber', required: false },
  { name: 'pincode', required: false },
  { name: 'city', required: false },
  { name: 'country', required: false },
  { name: 'website', required: false },
  { name: 'address', required: false },
  { name: 'assignUsers', required: true },
];

export function useAccountForm({
  open,
  selectedUser,
  handleSubmitData,
  customFields,
}: UseAccountFormProps) {
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);

  const { formKey, ...methods } = useHandleForm({
    isDialogOpen: open,
    module: 'company',
    fldList,
    customFields,
    selectedRecord: selectedUser,
  });

  const { setValue } = methods;

  const { assigneeProps } = useAssigneeField({
    multiFieldAssign: selectedUser ? selectedUser?.CompanyAssign : undefined,
    setValue: setValue as any,
  });

  const onSubmit = methods.handleSubmit(async (data: Partial<IAccountCreate>) => {
    const { assignUsers, ...rest } = data;

    const assignedPayload = buildAssignedPayload(assignUsers);
    const customFldData = extractCustomFields(rest);
    const auditFields = addAuditFields(
      !!selectedUser?.id,
      loginApiData && 'id' in loginApiData ? loginApiData.id : undefined
    );

    const accountPayload = {
      phone: formatPhoneNumber(data.phone) ?? '',
      name: rest.name ?? '',
      address: rest.address ?? '',
      crNumber: rest.crNumber ?? '',
      pincode: rest.pincode ?? '',
      city: rest.city ?? '',
      country: rest.country ?? '',
      website: rest.website ?? '',
      email: rest.email ?? '',
      customFields: customFldData,
      id: selectedUser?.id ?? '',
      ...auditFields,
    };

    handleSubmitData(accountPayload, assignedPayload);
  });

  return {
    formKey,
    methods,
    onSubmit,
    assigneeListProps: assigneeProps,
  };
}
